import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  engineChartList,
  sepChartList,
  electricChartList,
} from "../../model/slice/chartSettingsSlice.js";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

import { ColorPicker } from "./colorPicker.jsx";

import { updateLines } from "../../api/charts.js";
import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType.js";
import { COLORS } from "../../constants/colors.js";
import { DEVICE_TYPE, detectDevice } from "../../constants/detectDevice.js";
import { selectedTimezone } from "../../model/slice/timezoneSlice.js";
import { TIMEZONE } from "../../constants/timezone.js";
import { calDurations } from "../../constants/calDuration.js";
import { VESSEL_TYPE } from "../../constants/constants.js";
import { isDigitalChannel, isAnalogChannel } from "../../constants/channel/config.js";
import { DATE_AXIS_TOOLTIP_FORMAT } from "../../constants/util.js";
import { CHART } from "../../constants/trendGraph/chartType.js";
import { getLineInfo, handleTooltipLock, handleTooltipUnlocking } from "./chartHelper.js";
import { DATA_GAP, DATE_TIME_FORMAT, MONTH_DATE_FORMAT } from "../../constants/chart/chart.js";
import { addLegendFeatures, handleScrollBarByKeyboard, initCursor, initGanttYAxis, initLegend, initScrollbarX, initScrollbarY, initYAxis } from "./chartCommon.js";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { NAVIGATION_BAR_HEIGHT, TAB_BAR_HEIGHT } from "../../constants/layout.js";

let isLocked = false;

const AmCharts = React.memo((props) => {
  const {
    graphData = [],
    graphDataDigital = [],
    vesselId,
    type,
    chartType,
    isComparison = false,
    vesselData,
    customChartLines,
    customLineData,
    isMockPreview = false,
    expand,
    multipleGraphIndex,
    isVesselOverview,
    isDashboard = false,
    setChartRef,
    overviewTrendIdx,
  } = props;
  const numberOfDigitalChannel = customChartLines ? customChartLines.filter((channel) => isDigitalChannel(channel.channelType)).length : 0;
  const isMobile = [DEVICE_TYPE.PHONE, DEVICE_TYPE.TABLET].includes(detectDevice);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const refXYchart = useRef(null);
  const refDigitalChart = useRef(null);
  const chartRef = useRef();
  const ganttRef = useRef();
  const legendRef = useRef(null);
  const digitalLegendRef = useRef(null);
  const rootRef = useRef(null);
  const digitalRootRef = useRef(null);
  let chartid = expand == null ? "amchartdata" : "amchartdataexpand";
  let ganttid = expand == null ? "ganttdata" : "ganttdataexpand";
  let chartsToSync = [];
  if (multipleGraphIndex !== null) {
    chartid += multipleGraphIndex;
    ganttid += multipleGraphIndex;
  }
  if (isVesselOverview) {
    chartid += overviewTrendIdx;
    ganttid += overviewTrendIdx;
  }
  const engineChartSetting = useSelector(engineChartList);
  const sepChartSetting = useSelector(sepChartList);
  const electricChartSetting = useSelector(electricChartList);
  const timezone = useSelector(selectedTimezone);
  const [hideLineList, updateHideLineList] = useState([]);

  //for Color select trick
  const [displayColorPickerFlag, setDisplayColorPickerFlag] = useState(false);
  const [displayColorPicker, setDisplayColorPicker] = useState({
    flag: "",
    position: 0,
    isDigital: false,
  });
  const [dataKeyName, setDataKeyName] = useState("");
  const [displayDataKeyName, setDisplayDataKeyName] = useState('')
  const [isDigital, setIsDigital] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState({ x: 0, y: 0 });
  const [showAnalogChart, setShowAnalogChart] = useState(false);
  const [showDigitalChart, setShowDigitalChart] = useState(false);
  const [redrawChart, setRedrawChart] = useState(false);

  useEffect(() => {
    if (isComparison) {
      const currentHideLineList = JSON.parse(
        sessionStorage.getItem(`comparison-${chartType}-${vesselId}`)
      );
      if (!!currentHideLineList && currentHideLineList.length > 0) {
        updateHideLineList(currentHideLineList);
      }
    }
  }, []);

  useEffect(() => {
    if (!isComparison) {
      if (!customLineData) return;
      const currentHideLineList = Object.entries(customLineData)
        .filter(([key, value]) => "visible" in value && !value?.visible)
        .map(([key]) => key);
      if (!!currentHideLineList && currentHideLineList.length > 0) {
        updateHideLineList(currentHideLineList);
      }
    }
  }, [customLineData]);

  useEffect(() => {
    if (isComparison) {
      sessionStorage.setItem(`comparison-${chartType}-${vesselId}`, JSON.stringify(hideLineList));
    }
  }, [hideLineList]);

  useEffect(() => {
    let isRedraw = false;
    if (graphData?.length) {
      setShowAnalogChart(type !== GRAPH_TYPE.CUSTOM || customChartLines?.length - numberOfDigitalChannel > 0);
      isRedraw = true;
    }

    if (graphDataDigital?.length) {
      setShowDigitalChart(numberOfDigitalChannel > 0);
      isRedraw = true;
    }

    if (isRedraw) {
      setRedrawChart(true);
    }
  }, [graphData, graphDataDigital, customChartLines]);

  // amcharts
  useLayoutEffect(() => {
    if (!chartRef && !ganttRef) return;
    if (!vesselData) return;
    if (!showAnalogChart && !showDigitalChart && !redrawChart) return;

    // *** License From 2023/05/22 ***
    am5.addLicense(process.env.AMCHARTS_LICENSE);
    // ******

    let groot;
    let scrollbarX;
    let scrollbarY;
    if (showAnalogChart) {
      ({groot, scrollbarX, scrollbarY} = createXYChart());
    }
    let ganttroot;
    if (showDigitalChart) {
      ganttroot = createGanttChart();
    }

    const listener = {scrollbarX: scrollbarX, scrollbarY: scrollbarY, handleEvent: handleScrollBarByKeyboard}
    if(!isComparison && !isVesselOverview && !isMockPreview && !isDashboard){
      document.addEventListener("keydown", listener, false);
    }

    am5.array.each(chartsToSync, function(sourceChart) {
      const syncCursors = [];
      am5.array.each(chartsToSync, function(targetChart) {
        if (targetChart !== sourceChart) {
          syncCursors.push(targetChart.get("cursor"));
        }
      });
      sourceChart.get("cursor")?.set("syncWith", syncCursors);
    });

    setRedrawChart(false);
    
    return () => {
      document.removeEventListener("keydown", listener);
      chartsToSync = [];
      groot?.dispose();
      ganttroot?.dispose();
    };
  }, [vesselData, ganttRef, chartRef, showAnalogChart, showDigitalChart, timezone, redrawChart]);

  useEffect(() => {
    //for User change line color trick
    if (displayColorPicker.flag == "" || displayColorPicker.position == 0) {
      // Do nothing
    } else {
      if(displayColorPicker.isDigital) {
        refDigitalChart.current.series.each(function (item, index) {
          if (displayColorPicker.flag == item.get('key')) {
            //color change trick
            item.set("fill", am5.color(displayColorPicker.position));
            item.set("stroke", am5.color(displayColorPicker.position));

            // necessary for column series to set color using both item.set() and item.columns.template.setAll()
            if (item instanceof am5xy.ColumnSeries) {
              item.columns.template.setAll({
                fill: am5.color(displayColorPicker.position),
                stroke: am5.color(displayColorPicker.position),
              });
            }
          }
          digitalLegendRef.current.data.setAll(refDigitalChart.current.series.values);
          addLegendFeatures(digitalRootRef.current, refDigitalChart.current, digitalLegendRef.current);
        })
      }else{
        refXYchart.current.series.each(function (item, index) {
          if (displayColorPicker.flag == item.get('key')) {
            //color change trick
            item.set("fill", am5.color(displayColorPicker.position));
            item.set("stroke", am5.color(displayColorPicker.position));
          }
        });
        legendRef.current.data.setAll(refXYchart.current.series.values);
        addLegendFeatures(rootRef.current, refXYchart.current, legendRef.current);
      }
      refXYchart.current.series.each(function (series, i) {
        if (series instanceof am5xy.LineSeries && series.get("isDigitalChannel")) {
          if (series.get("legendDataItem")) series.get("legendDataItem").dispose();
        }
      });
    }
  }, [displayColorPicker]);

  const createXYChart = () => {
    const groot = am5.Root.new(chartid, {
      useSafeResolution: false,
    });
    groot.utc = timezone === TIMEZONE.UTC; //set time zone to UTC
    groot.setThemes([am5themes_Animated.new(groot)]);
    rootRef.current = groot;
    const touchOptions = isMobile ? {
      pinchZoomX: true,
      pinchZoomY: true,
      panX: true,
      panY: true,
    } : {
      panX: false,
      panY: false,
    };
    const xyChart = groot.container.children.push(
      am5xy.XYChart.new(groot, {
        wheelY: isMockPreview ? "none" : "zoomX",
        maxTooltipDistance: -1,
        layout: groot.verticalLayout,
        ...touchOptions,
      })
    );

    chartsToSync.push(xyChart);

    // we will optimize this isOneSec options, but it need to change a lot of area...
    const duration = calDurations({
      startDate: graphData[0]?.dateTime,
      endDate: graphData[graphData?.length-1]?.dateTime
    });
    const checkOneSec = ((vesselData.vesselType === VESSEL_TYPE.SEP || vesselData?.NoTank > 0) && duration === 0) || duration === 0;

    const linesInfo = getLineInfo({
      vesselData,
      chartType,
      dataList: currentDataList(),
      isComparison,
      type,
    });
    const ganttOnly = type === GRAPH_TYPE.CUSTOM ? !linesInfo?.some(line => isAnalogChannel(line?.channelType)) : false;
    const lineOnly = type === GRAPH_TYPE.CUSTOM ? !linesInfo?.some(line => isDigitalChannel(line?.channelType)) : true;

    if (!ganttOnly && !lineOnly) xyChart.leftAxesContainer.set("layout", groot.verticalLayout);

    //Scrollbar X
    const scrollbarX = initScrollbarX(xyChart, groot, isMockPreview, isLocked, handleUnlock);
    //Scrollbar Y
    const scrollbarY = initScrollbarY(xyChart, groot, isMockPreview, isLocked, handleUnlock);
    const valueAxis = initYAxis(groot, xyChart, ganttOnly, lineOnly);
    const dateAxis = initXAxis(groot, xyChart, checkOneSec, duration, syncAxes);

    xyChart.events.on("boundschanged", function() {
      syncTitles();
    });
  
    //Data prepare (replace null with '--' to display)
    const dispData = Array.isArray(graphData) ? graphData.map((list) => Object.fromEntries(Object.entries(list).map(([k,v]) => [k,(v === null || v === undefined) ? '--' : v]))) : [];
    ConvertDateForDisplayData(dispData);
  
    //Each LineSeries and setting
    initLineSeries(
      dispData,
      linesInfo,
      xyChart,
      groot,
      dateAxis,
      valueAxis,
      vesselId,
      isComparison,
      customLineData,
      isVesselOverview,
      isDashboard,
    );
  
    if (!isMockPreview) {
      //Cursor
      initCursor(xyChart, groot, dateAxis, isMockPreview, isMobile);
    }

    //legend : Because of the drawing order, LEGEND needs to be here.
    if (!isVesselOverview && !isDashboard) {
      const legend = initLegend(
        chartsToSync,
        xyChart,
        groot,
        setDataKeyName,
        setDisplayDataKeyName,
        setIsDigital,
        setColorPickerPos,
        setDisplayColorPicker,
        setDisplayColorPickerFlag,
        type,
        false,
        chartType,
      );
  
      if (isMockPreview) {
        legend.hide(0);
      }
      legendRef.current = legend;
    }
    //Click stop trick : START
  
    if (!isMockPreview && !isVesselOverview && !isDashboard && !isMobile) {
      xyChart.plotContainer.events.on("click", function (ev) {
        if (isLocked) {
          handleUnlock();
        } else {
          handleLock(CHART.ANALOG);
        }
      });
      //Click stop trick : END
  
      //click zoom out button also
      xyChart.zoomOutButton.events.on("click", (e) => {
        if (isLocked) {
          handleUnlock();
        }
      });
  
      // follow the locked cursor position change by maximize
      xyChart.plotContainer.events.on("boundschanged", (e) => {
        if (isLocked) handleLock(CHART.ANALOG);
      });
    }

    //set ref xyChart for redraw function
    refXYchart.current = xyChart;
    rootRef.current = groot;

    if (isVesselOverview) {
      setChartRef(xyChart);
    }

    return {groot, scrollbarX, scrollbarY};
  }
  
  const createGanttChart = () => {
    const ganttroot = am5.Root.new(ganttid, {
      useSafeResolution: false,
    });
    ganttroot.utc = timezone === TIMEZONE.UTC; //set time zone to UTC
    ganttroot.setThemes([am5themes_Animated.new(ganttroot)]);
    digitalRootRef.current = ganttroot;
    const touchOptions = isMobile ? {
      pinchZoomX: true,
      pinchZoomY: false,
      panX: true,
      panY: false,
    } : {
      panX: false,
      panY: false,
    };

    // we will optimize this isOneSec options, but it need to change a lot of area...
    const duration = calDurations({
      startDate: graphData[0]?.dateTime,
      endDate: graphData[graphData?.length-1]?.dateTime
    });
    const checkOneSec = (vesselData.vesselType === VESSEL_TYPE.SEP || vesselData?.NoTank > 0) && duration === 0;
  
    const linesInfo = getLineInfo({
      vesselData,
      chartType,
      dataList: currentDataList(),
      isComparison,
      type,
    });
    const ganttOnly = type === GRAPH_TYPE.CUSTOM ? !linesInfo?.some(line => isAnalogChannel(line?.channelType)) : false;
    const lineOnly = type === GRAPH_TYPE.CUSTOM ? !linesInfo?.some(line => isDigitalChannel(line?.channelType)) : true;
  
    const dispData = Array.isArray(graphData) ? graphData.map((list) => ({ ...list })) : [];
    const dispDataDigitalInit = graphDataDigital.map((list) => ({ ...list }));
    ConvertDateForDisplayData(dispData);
    const dispDataDigital = makeDigitalData(dispDataDigitalInit, linesInfo);
    const digitalLines = linesInfo.filter((line) => isDigitalChannel(line.channelType));

    const xyChart = ganttroot.container.children.push(
      am5xy.XYChart.new(ganttroot, {
        wheelY: isMockPreview ? "none" : "zoomX",
        maxTooltipDistance: -1,
        layout: ganttroot.verticalLayout,
        height: (numberOfDigitalChannel * 35) + 100,
        ...touchOptions,
      })
    );

    chartsToSync.push(xyChart);

    //Scrollbar X
    initScrollbarX(xyChart, ganttroot, isMockPreview, isLocked, handleUnlock);

    xyChart.rightAxesContainer.children.push(am5.Rectangle.new(ganttroot, {
      width: 38
    }));

    const valueAxis = initYAxis(ganttroot, xyChart, true, lineOnly, true);
    const dateAxis = initXAxis(ganttroot, xyChart, checkOneSec, duration, syncAxes);

    valueAxis.events.on("boundschanged", function() {
      syncTitles();
    });
  
    xyChart.leftAxesContainer.set("layout", ganttroot.verticalLayout);
    
    for(let i=0; i< digitalLines.length; i++){
      const valueAxisGantt = initGanttYAxis(ganttroot, xyChart, isComparison, isVesselOverview, ganttOnly);
      initGanttSeries(
        dispDataDigital[i].graphData,
        digitalLines[i],
        xyChart,
        ganttroot,
        dateAxis,
        valueAxisGantt,
        vesselId,
        isComparison,
        customLineData
      );
    }
  
    if (!isMockPreview) {
      //Cursor
      initCursor(xyChart, ganttroot, dateAxis, isMockPreview, isMobile, true);
    }

    //legend : Because of the drawing order, LEGEND needs to be here.
    if (!isVesselOverview && !isDashboard) {
      const legend = initLegend(
        chartsToSync,
        xyChart,
        ganttroot,
        setDataKeyName,
        setDisplayDataKeyName,
        setIsDigital,
        setColorPickerPos,
        setDisplayColorPicker,
        setDisplayColorPickerFlag,
        type,
        true,
        chartType,
      );
  
      if (isMockPreview) {
        legend.hide(0);
      }
      digitalLegendRef.current = legend;
    }
    //Click stop trick : START
  
    if (!isMockPreview && !isVesselOverview && !isMobile) {
      xyChart.plotContainer.events.on("click", function (ev) {
        if (isLocked) {
          handleUnlock();
        } else {
          handleLock(CHART.DIGITAL);
        }
      });
      //Click stop trick : END
  
      //click zoom out button also
      xyChart.zoomOutButton.events.on("click", (e) => {
        if (isLocked) {
          handleUnlock();
        }
      });
  
      // follow the locked cursor position change by maximize
      xyChart.plotContainer.events.on("boundschanged", (e) => {
        if (isLocked) handleLock(CHART.DIGITAL);
      });
    }
    refDigitalChart.current = xyChart;
    return ganttroot;
  }

  function syncTitles() {
    if (chartsToSync?.length < 2) return;
    let maxWidth = 0;
    chartsToSync.forEach((ch) => {
      const width = ch.leftAxesContainer.width();
      if (width > maxWidth) {
        maxWidth = width;
      }
    });

    chartsToSync.forEach((ch) => {
      ch.leftAxesContainer.set("minWidth", maxWidth);
    });
  }
  
  const currentDataList = () => {
    switch (type) {
      case GRAPH_TYPE.ELECTRIC:
        return electricChartSetting[chartType]
      case GRAPH_TYPE.ENGINE:
        return engineChartSetting.filter((d) => d.chartType === chartType);
      case GRAPH_TYPE.SEP:
        return sepChartSetting.filter((d) => d.chartType === chartType);
      case GRAPH_TYPE.CUSTOM:
        return customChartLines;
    }
  };
  //amCharts functions START
  const initLineSeries = (
    displayData,
    inlinesInfo,
    chart,
    root,
    inxAxis,
    inyAxis,
    vesselId,
    isComparison,
    customLineData,
    isVesselOverview,
    isDashboard,
  ) => {
    inlinesInfo.forEach((oneLineInfo) => {
      if (!oneLineInfo) {
        if (isMockPreview) {
          inxAxis.hide(0)
          inyAxis.hide(0)
          const emptyChart = chart.series.push(
            am5xy.LineSeries.new(root, {
              valueXField: "a",
              valueYField: "b",
              xAxis: inxAxis,
              yAxis: inyAxis,
              visible: false,
            })
          );
          emptyChart.data.setAll([{"a": 1689897614000, "b": 20}])
        }
        return;
      }
      if (isDigitalChannel(oneLineInfo.channelType)) return;  
      const key = oneLineInfo.keyName;
      inxAxis.show();
      inyAxis.show();
      const hiddenFlag = hideLineList.includes(key);
      let color = "";
      if (isComparison) {
        color = sessionStorage.getItem(`comparison-${chartType}-${vesselId}-${key}`)
          ? sessionStorage.getItem(`comparison-${chartType}-${vesselId}-${key}`)
          : oneLineInfo.color;
      } else {
        color = customLineData?.[key]?.color ?? oneLineInfo.color;
      }

      const valueSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          key,
          name: oneLineInfo.name,
          valueXField: "dateTime",
          valueYField: key,
          xAxis: inxAxis,
          yAxis: inyAxis,
          visible: !hiddenFlag,
          calculateAggregates: true,
          fill: am5.color(color),
          stroke: am5.color(color),
          legendLabelText: type === GRAPH_TYPE.CUSTOM ? `${oneLineInfo.key?.replace(/\"/g, '')}_${oneLineInfo.name}` : oneLineInfo.name,
          legendValueText: "{valueY}",
          legendRangeValueText: "  ",
          connect: isVesselOverview,
          autoGapCount: DATA_GAP,
          isDigitalChannel: false,
        })
      );
      if (hideLineList.includes(key)) {
        valueSeries.hide();
      }
      if (isDashboard) {
        const tooltip = am5.Tooltip.new(root, {
            labelText: `${oneLineInfo.name}: {valueY}`,
            pointerOrientation: "vertical",
        });

        valueSeries.set("tooltip", tooltip);
      }
      valueSeries.set("isDigitalChannel", false);
      valueSeries.on("visible", async (visible, target) => {
        const targetName = target.get("valueYField");
        if (visible) {
          updateHideLineList((prev) => prev.filter((d) => d !== targetName));
        } else {
          updateHideLineList((prev) => prev.concat([targetName]));
        }
        if (!isComparison) {
          await updateLines(vesselId, type, chartType, targetName, null, visible);
        }
      });
      valueSeries.data.setAll(displayData);
    });
  };

  const initGanttSeries = (
    displayData,
    oneLineInfo,
    chart,
    root,
    inxAxis,
    inyAxis,
    vesselId,
    isComparison,
    customLineData
  ) => {
    if (Object.keys(displayData).length === 0) {
      inxAxis.hide(0)
      inyAxis.hide(0)
      return;
    }
    if (!oneLineInfo) {
      if (isMockPreview) {
        inxAxis.hide(0)
        inyAxis.hide(0) 
      }
      return;
    }
    if (isDigitalChannel(oneLineInfo?.channelType)) {
      const key = oneLineInfo.keyName;
      inxAxis.show()
      inyAxis.show()
      const hiddenFlag = hideLineList.includes(key);
      let color = "";
      if (isComparison) {
        color = sessionStorage.getItem(`comparison-${chartType}-${vesselId}-${key}`)
          ? sessionStorage.getItem(`comparison-${chartType}-${vesselId}-${key}`)
          : oneLineInfo.color;
      } else {
        color = customLineData?.[key]?.color ?? oneLineInfo.color;
      }
      const ganttSeries = chart.series.push(
        am5xy.StepLineSeries.new(root, {
          key,
          connect: false,
          name: oneLineInfo.name,
          valueXField: "dateTime",
          valueYField: "value",
          xAxis: inxAxis,
          yAxis: inyAxis,
          visible: !hiddenFlag,
          fill: am5.color(color),
          stroke: am5.color(color),
          legendLabelText: oneLineInfo.name,
          legendValueText: "{status}",
          legendRangeValueText: "  ",
          autoGapCount: DATA_GAP,
          isDigitalChannel: true,
        })
      );
      ganttSeries.strokes?.template.setAll({
              strokeWidth: 3,
            });
      if (hideLineList.includes(key)) {
        ganttSeries.hide();
      }
      ganttSeries.on("visible", async (visible, target) => {
        const targetName = target.get("valueYField");
        if (visible) {
          updateHideLineList((prev) => prev.filter((d) => d !== targetName));
        } else {
          updateHideLineList((prev) => prev.concat([targetName]));
        }
        if (!isComparison) {
          await updateLines(vesselId, type, chartType, targetName, null, visible);
        }
      });
      ganttSeries.data.setAll(displayData);
    }
  };

  //amCharts functions END
  const ConvertDateForDisplayData = (testData) =>
    testData.map((value) => {
      value.dateTime = Date.parse(value["dateTime"]);
      return value;
    });
  
  const makeDigitalData = (digitalData, linesInfo) => {
    const digitalLines = linesInfo.filter(l => isDigitalChannel(l?.channelType));
    const newData = [];
    digitalLines.forEach((line, i) => {
      const temp = [];
      digitalData.forEach((value) => {
        const isSelected = value[line.key] === line.digitalVar ? 1 : 0
        temp.push({dateTime: Date.parse(value["dateTime"]), value: value[line.key] === undefined ? null : isSelected, status: value[line.key] === undefined ? null : isSelected ? line?.selectedStatus : line?.unSelectedStatus})
      })
      newData.push({key: line.key, graphData: temp})
    });

    return newData;
  };

  function syncAxes(targetChart) {
    const targetAxis = targetChart.xAxes.getIndex(0);
    if (targetAxis._skipSync != true) {
      const start = targetAxis.get("start");
      const end = targetAxis.get("end");
      am5.array.each(chartsToSync, function(chart) {
        if (chart != targetChart) {
          const axis = chart.xAxes.getIndex(0);
          axis._skipSync = true;
          axis.setAll({
            start,
            end
          })
          axis._skipSync = false;
        }
      });
    }
  }

  const handleLock = (chType) => {
    isLocked = true;
    handleTooltipLock(chType, chartsToSync);
  }

  const handleUnlock = () => {
    isLocked = false;
    handleTooltipUnlocking(chartsToSync);
  }

  // TODO Need to update this to set height based on the parent container's height
  // const PAPER_HEIGHT = `calc(100vh - ${NAVIGATION_BAR_HEIGHT}px - ${TAB_BAR_HEIGHT}px - ${GRAPH_TYPE.CUSTOM === type ? 200 : 150}px - ${TITLE_PAPER_HEIGHT}px)`;
  const PAPER_HEIGHT = '100%';

  let analogChartHeight = PAPER_HEIGHT;
  if (showDigitalChart) {
    if (isDashboard) {
      if (showDigitalChart) {
        analogChartHeight = `calc(100% - 130px - ${(numberOfDigitalChannel) * 35}px)`;
      } else {
        analogChartHeight = '100%';
      }
    } else {
      analogChartHeight = '450px';
    }
  } else if (isComparison) {
    analogChartHeight = `calc(50vh - ${NAVIGATION_BAR_HEIGHT}px - ${TAB_BAR_HEIGHT}px)`;
  } else if (isVesselOverview) {
    if (isSm) {
      analogChartHeight = '200px';
    } else {
      analogChartHeight = '225px';
    }
  }

  return (
    <Box component={"div"} sx={{position: "relative", height: '100%'}}>
      {showAnalogChart && <Box
        component={"div"}
        id={chartid}
        ref={chartRef}
        data-testid="amcharts"
        sx={{
          overflow: 'auto',
          width: "100%",
          height: analogChartHeight,
        }}
      />}
      {showDigitalChart && <Box
        component={"div"}
        id={ganttid}
        data-testid="amchartsgantt"
        sx={{
          overflow: 'auto',
          width: "100%",
          height: `calc(130px + ${(numberOfDigitalChannel) * 35}px)`,
        }}
      />}
      {!isVesselOverview && <Box
        className="colorPickerContainer"
        sx={{
          width: "fit-content",
          visibility: displayColorPickerFlag ? "visible" : "hidden",
          position: "absolute",
          left: colorPickerPos.x,
          top: colorPickerPos.y,
        }}
      >
        <Box sx={{ left: `10px` }}>
          <ColorPicker
            setDisplayColorPicker={setDisplayColorPicker}
            graphType={type}
            dataKey={dataKeyName}
            displayDataKeyName={displayDataKeyName}
            isDigital={isDigital}
            setOnClose={setDisplayColorPickerFlag}
            chartType={chartType}
            isComparison={isComparison}
            vesselId={vesselId}
          />
        </Box>
      </Box>}
    </Box>
  );
});

//export amCharts common init functions
export const initXAxis = (
  root,
  chart,
  isOneSec = false,
  interval = 1,
  syncAxes,
) => {
  // Gantt chart cannot use gapless date axis
  const dateAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        minZoomCount:7,
        baseInterval: {
          timeUnit: isOneSec ? "second" : "minute",
          count: 1,
        },
        groupData: true,
        groupCount: 3600,
        tooltip: am5.Tooltip.new(root, {}),
        tooltipDateFormat: DATE_AXIS_TOOLTIP_FORMAT,
        renderer: am5xy.AxisRendererX.new(root, { }),
      })
    );
  dateAxis.get("dateFormats")["month"] = MONTH_DATE_FORMAT;
  dateAxis.get("periodChangeDateFormats")["month"] = MONTH_DATE_FORMAT;
  dateAxis.get("dateFormats")["week"] = MONTH_DATE_FORMAT;
  dateAxis.get("periodChangeDateFormats")["week"] = MONTH_DATE_FORMAT;
  dateAxis.get("dateFormats")["day"] = MONTH_DATE_FORMAT;
  dateAxis.get("periodChangeDateFormats")["day"] = MONTH_DATE_FORMAT;
  dateAxis.get("dateFormats")["hour"] = DATE_TIME_FORMAT;
  dateAxis.get("periodChangeDateFormats")["hour"] = DATE_TIME_FORMAT;
  dateAxis.get("dateFormats")["minute"] = DATE_TIME_FORMAT;
  dateAxis.get("periodChangeDateFormats")["minute"] = DATE_TIME_FORMAT;
  dateAxis.get("dateFormats")["second"] = DATE_TIME_FORMAT;
  dateAxis.get("periodChangeDateFormats")["second"] = DATE_TIME_FORMAT;
  const xRenderer = dateAxis.get("renderer");
  xRenderer.labels.template.setAll({
    fill: am5.color(COLORS.white),
    opacity: 0.2,
    fontSize: "12px",
    paddingTop: 14,
    centerX: am5.percent(20),
  });
  xRenderer.grid.template.setAll({
    stroke: am5.color(COLORS.white),
    strokeWidth: 2,
    strokeDasharray: [4, 2],
  });
  const rangeDataItem = dateAxis.makeDataItem({
    value: 0,
  });
  const range = dateAxis.createAxisRange(rangeDataItem);

  range.get("label").setAll({
    text: "",
    maxPosition: 1,
  });

  range.get("grid").setAll({
    stroke: am5.color(0x000000),
    strokeOpacity: 0.1,
    location: 1,
  });
  dateAxis.onPrivate("selectionMax", function (max) {
    if (max) {
      rangeDataItem.set("value", max);
      range.get("label").setAll({});
    }
  });

  dateAxis.on("start", (evt) => {
    syncAxes(chart);
  });
  dateAxis.on("end", (evt) => {
    syncAxes(chart);
  });

  return dateAxis;
};

AmCharts.propTypes = {
  vesselData: PropTypes.object,
  expand: PropTypes.string,
  type: PropTypes.string,
  isNoData: PropTypes.bool,
  graphData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  graphDataDigital: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  enginePosition: PropTypes.string,
  chartType: PropTypes.string,
  vesselId: PropTypes.string,
  isInitialLoad: PropTypes.bool,
  isComparison: PropTypes.bool,
  multipleGraphIndex: PropTypes.number,
  isVesselOverview: PropTypes.bool,
  isDashboard: PropTypes.bool,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
  customChartLines: PropTypes.array,
  customLineData: PropTypes.array,
  isMockPreview: PropTypes.bool,
};

export default AmCharts;
